import type { AxiosError } from 'axios';
import { Menu } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Unit } from '@/api/generated/models';
import {
  getListUnitsQueryKey,
  useActivateUnit,
  useDeactivateUnit,
  useDeleteUnit
} from '@/api/generated/units/units';
import { ConfirmActionModal, KebabTrigger } from '@/components/common';
import { successNotificationIcon } from '@/theme/notificationIcons';

const unitErrorMessage = (error: unknown, fallback: string): string => {
  const backendMessage = (error as AxiosError<{ error?: string }>)?.response?.data?.error;
  return typeof backendMessage === 'string' ? backendMessage : fallback;
};

interface UnitActionsMenuProps {
  unit: Unit;
  onEdit: () => void;
}

/** Uredi/Deaktiviraj-Aktiviraj/Obriši za jednu jedinicu — jedini akcijski
 * izbornik na listi (usklađeno s ObjectRegistryPage/OfferDetailPage
 * konvencijom ove sesije: akcije koje mijenjaju stanje žive uz redak, ne
 * kao gumbi unutar edit forme). Deaktiviranje i brisanje traže potvrdu
 * (ConfirmActionModal); aktiviranje je izravno, bez potvrde — isti obrazac
 * kao ObjectDetailPage/PriceListDetailPage. */
export const UnitActionsMenu = ({ unit, onEdit }: UnitActionsMenuProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const invalidateList = () => {
    queryClient.invalidateQueries({ queryKey: getListUnitsQueryKey() });
  };

  const activateMutation = useActivateUnit({
    mutation: {
      onSuccess: () => {
        invalidateList();
        notifications.show({
          color: 'green',
          icon: successNotificationIcon,
          message: t('adminUnits.activateSuccess')
        });
      }
    }
  });

  const deactivateMutation = useDeactivateUnit({
    mutation: {
      onSuccess: () => {
        invalidateList();
        notifications.show({
          color: 'green',
          icon: successNotificationIcon,
          message: t('adminUnits.deactivateSuccess')
        });
        setDeactivateModalOpen(false);
      }
    }
  });

  const deleteMutation = useDeleteUnit({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        invalidateList();
        notifications.show({
          color: 'green',
          icon: successNotificationIcon,
          message: t('adminUnits.deleteSuccess')
        });
        setDeleteModalOpen(false);
      },
      onError: (error) => {
        notifications.show({
          color: 'red',
          message: unitErrorMessage(error, t('common.error'))
        });
      }
    }
  });

  return (
    <>
      <Menu position="bottom-end" withinPortal shadow="md" radius="md" width={200}>
        <Menu.Target>
          <KebabTrigger onClick={(event) => event.stopPropagation()} />
        </Menu.Target>
        <Menu.Dropdown onClick={(event) => event.stopPropagation()}>
          <Menu.Item styles={{ item: { color: 'var(--color-text-primary)' } }} onClick={onEdit}>
            {t('adminUnits.editLink')}
          </Menu.Item>
          {unit.isActive ? (
            <Menu.Item
              styles={{ item: { color: 'var(--color-status-danger)' } }}
              onClick={() => setDeactivateModalOpen(true)}
            >
              {t('adminUnits.deactivateAction')}
            </Menu.Item>
          ) : (
            <Menu.Item
              styles={{ item: { color: 'var(--color-status-success)' } }}
              onClick={() => unit.id && activateMutation.mutate({ id: unit.id })}
            >
              {t('adminUnits.activateAction')}
            </Menu.Item>
          )}
          <Menu.Item
            styles={{ item: { color: 'var(--color-status-danger)' } }}
            onClick={() => setDeleteModalOpen(true)}
          >
            {t('adminUnits.deleteAction')}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      <ConfirmActionModal
        opened={deactivateModalOpen}
        onClose={() => setDeactivateModalOpen(false)}
        onConfirm={() => unit.id && deactivateMutation.mutate({ id: unit.id })}
        loading={deactivateMutation.isPending}
        isPaused={deactivateMutation.isPaused}
        danger
        title={t('adminUnits.deactivateTitle')}
        message={t('adminUnits.deactivateMessage', { code: unit.code })}
        confirmLabel={t('adminUnits.deactivateConfirm')}
      />

      <ConfirmActionModal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={() => unit.id && deleteMutation.mutate({ id: unit.id })}
        loading={deleteMutation.isPending}
        isPaused={deleteMutation.isPaused}
        danger
        title={t('adminUnits.deleteTitle')}
        message={t('adminUnits.deleteMessage', { code: unit.code })}
        confirmLabel={t('adminUnits.deleteConfirm')}
      />
    </>
  );
};
